import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Formik, Field, ErrorMessage } from 'formik';
import { useDispatch } from 'react-redux';

import { isLoading, isFailed, ProgressButton } from '@parapet/common';
import { authFormValidatorWithPassword } from '@app/shared/utils/validators';
import { authUser } from '@app/store/user/actions';
import { AuthTypeSelector } from '@app/shared/components/AuthTypeSelector';
import { useTypedSelector } from '@app/store/root';
import { InputText } from '@app/shared/components/InputText';
import {
  FormRowError,
  LoginAuthError,
} from '@app/shared/components/FormRowError';
import { PhoneWithContryCodeInput } from '@app/components/PhoneWithCountryCodeInput';
import { CountryCodeExtended } from '@app/components/PhoneWithCountryCodeInput/models';
import { setSmallChatEmail } from '@app/shared/utils/smallchat';
import { StyledLoginForm, LoginFormFieldset, LoginFormRow } from './styles';

export function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export const LoginForm: React.FC = () => {
  const dispatch = useDispatch();
  const status = useTypedSelector(({ user }) => user.authStatus);
  const queryEntries = Array.from(useQuery().entries());
  const [authType, setAuthType] = useState<'email' | 'phone'>('email');
  const [submitted, setSubmitted] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const isEmailAuthType = authType === 'email';

  // Set cached auth type on mount
  useEffect(() => {
    const authType = localStorage.getItem('auth-type');
    if (authType !== null) {
      setAuthType(authType as 'email' | 'phone');
    }
  }, []);

  // Focus associated input when auth type changes
  useEffect(() => {
    setTimeout(() => {
      const selector = isEmailAuthType ? 'email' : 'tel';
      const el = document.querySelector(`input[type="${selector}"]`);
      (el as HTMLElement | null)?.focus();
    }, 600);
    localStorage.setItem('auth-type', authType);
  }, [authType]);

  let company: string;
  let callback: string;

  if (queryEntries.length) {
    const [companyParam, callbackParam] = queryEntries;
    company = companyParam[1];
    callback = callbackParam[1];
  }

  const loading = isLoading(status);

  return (
    <Formik
      enableReinitialize={true}
      initialValues={{
        email: '',
        password: '',
        phone: '',
        countryCode: null as CountryCodeExtended | null,
      }}
      validate={(values) =>
        authFormValidatorWithPassword(values, isEmailAuthType, phoneError)
      }
      onSubmit={(values, actions) => {
        actions.setSubmitting(false);
        const payload = { ...values, authType, company, callback };
        // Ensure phone starts with country code
        if (!isEmailAuthType && values.countryCode) {
          payload.phone = values.countryCode.code + '' + values.phone;
        }
        dispatch(authUser(payload));
      }}
    >
      {(form) => (
        <StyledLoginForm
          autoSave="false"
          autoComplete="false"
          onChange={() => setSmallChatEmail(form.values.email)}
        >
          <AuthTypeSelector authType={authType} setAuthType={setAuthType} />

          <LoginFormFieldset>
            <LoginFormRow>
              <div
                className={[
                  'animated-input-row',
                  isEmailAuthType ? 'is-email' : 'is-phone',
                ].join(' ')}
              >
                <Field
                  className={[
                    'email-input-group',
                    submitted ? 'submitted' : '',
                  ].join(' ')}
                  type="email"
                  name="email"
                  aria-hidden={!isEmailAuthType}
                  autoComplete="email"
                  placeholder="Email address"
                  as={InputText}
                  tabIndex={1}
                  required
                  autoFocus
                />

                <PhoneWithContryCodeInput
                  valueChange={(phone, error, countryCode) => {
                    setPhoneError(error);
                    // Requires a delay for state to settle
                    setTimeout(() => {
                      form.setFieldValue('phone', phone);
                      form.setFieldValue('countryCode', countryCode);
                    });
                  }}
                />
              </div>

              {submitted && isEmailAuthType && (
                <ErrorMessage name="email" component={FormRowError} />
              )}

              {form.errors.phone && (
                <FormRowError>{form.errors.phone}</FormRowError>
              )}
            </LoginFormRow>

            <LoginFormRow style={{ marginBottom: '36px' }}>
              <Field
                className={submitted && 'submitted'}
                type="password"
                name="password"
                placeholder="Password"
                as={InputText}
                tabIndex={2}
                disabled={loading}
                required
              />
              {submitted && (
                <ErrorMessage
                  data-testid="password-error"
                  name="password"
                  component={FormRowError}
                />
              )}
            </LoginFormRow>

            {isFailed(status) && (
              <LoginAuthError>
                <b>Login Error</b>
                Please check your credentials and try again.
              </LoginAuthError>
            )}

            <ProgressButton
              loading={loading}
              onClick={() => {
                form.submitForm();
                setSubmitted(true);
              }}
            >
              Login
            </ProgressButton>
          </LoginFormFieldset>
        </StyledLoginForm>
      )}
    </Formik>
  );
};
